import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type {
  InputMaybe,
  UpdateDocumentAssessmentResultMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

export interface UpdateDocumentAssessmentResultTRPCInput {
  Id: string;
  Rating?: InputMaybe<number>;
  Rationale?: InputMaybe<string>;
  TestDate?: InputMaybe<string>;
  CustomAttributeData?: InputMaybe<CustomAttributeData>;
}

/**
 * Maps TRPC mutation response to match the GraphQL mutation structure.
 * The tRPC update either succeeds on exactly one row or throws, so a
 * successful response always maps to affected_rows: 1.
 */
const mapTrpcResponseToGraphQL = (): UpdateDocumentAssessmentResultMutation => {
  return {
    update_document_assessment_result: {
      __typename: 'document_assessment_result_mutation_response',
      affected_rows: 1,
    },
  };
};

export const useUpdateDocumentAssessmentResultTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.documentAssessmentResult.update.mutationOptions({
      onSuccess: async () => {
        // Invalidation parity with the legacy Apollo evictFields:
        // document_assessment_result (+ aggregate), assessment,
        // internal_audit_report and oversight_assessment.
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.documentAssessmentResult.pathKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.assessment.pathKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.internalAuditReport.pathKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.oversight.pathKey(),
          }),
        ]);
      },
    }),
  });

  return {
    updateDocumentAssessmentResult: async (
      variables: UpdateDocumentAssessmentResultTRPCInput
    ): Promise<UpdateDocumentAssessmentResultMutation> => {
      await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL();
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
